import { Link } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useVisibleQuickActions } from '../hooks/useVisibleQuickActions';
import type { QuickAction } from '../lib/quickActions';

/**
 * The module panel's quick-actions section (M3 · 86d44z1xm).
 *
 * Renders NOTHING — heading included — when this user has no actions for this module. A
 * "Quick actions" heading standing over empty space is the failure the ticket calls out.
 */
export function QuickActions({ moduleId }: { moduleId: string }) {
  const actions = useVisibleQuickActions(moduleId);
  if (actions.length === 0) return null;

  return (
    <>
      <p className="m-0 px-2.5 pb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Quick actions
      </p>
      <div className="flex flex-col gap-0.5 pb-1.5">
        {actions.map((action) => (
          <QuickActionRow key={action.id} action={action} />
        ))}
      </div>
    </>
  );
}

function QuickActionRow({ action }: { action: QuickAction }) {
  const Icon = action.icon;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={action.href}
          className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sidebar-foreground transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        >
          {/* Capitol Red leading glyph — a §3 brand-fixed exception, and the one thing that
              separates a quick action from a nav row at a glance. */}
          <Icon className="h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">{action.label}</span>
        </Link>
      </TooltipTrigger>
      {/* The label is a bare verb ("Add user"); the hint says what actually happens on arrival. */}
      <TooltipContent side="right">{action.hint}</TooltipContent>
    </Tooltip>
  );
}
